import type { BuyerLeadDnDColumnFragment } from '@graphql/__generated__/graphql';

type Lead = BuyerLeadDnDColumnFragment;
type LotShape = NonNullable<Lead['lot']>;
type UserShape = NonNullable<Lead['user']>;
type MlsUserShape = NonNullable<Lead['mls_user']>;

// Structural input: any object with a UserCard-shaped `user` and a
// users_view-shaped `mls_user`. Lets the helper be called from queries
// that don't pull the full BuyerLeadDnDColumn fragment, as long as their
// selection sets match the underlying fragments.
type LeadWithEffectiveUser = {
  user?: UserShape | null;
  mls_user?: MlsUserShape | null;
};

/**
 * For MLS-context buyer leads where the owner-tenant `user` relationship
 * is hidden by RLS, fall back to the cross-tenant-safe `mls_user`
 * relationship (users_view, narrow column allowlist). Returns a synthetic
 * `users`-shaped object so downstream components (UserCard) keep working
 * without a separate MLS code path.
 *
 * The shape is constructed to match the `UserCard_user` fragment exactly
 * — single-element phone/email/image arrays built from the inlined
 * scalars exposed on users_view.
 */
export const getEffectiveUser = (
  lead: LeadWithEffectiveUser,
): UserShape | null => {
  if (lead.user != null) {
    return lead.user;
  }
  const m = lead.mls_user;
  if (m == null || m.id == null) {
    return null;
  }
  const synthetic: UserShape = {
    id: m.id,
    first_name: m.first_name ?? null,
    last_name: m.last_name ?? null,
    full_name: m.full_name ?? null,
    user_images:
      m.profile_image_url != null
        ? [
            {
              id: `mls-${m.id}`,
              image: {
                id: `mls-${m.id}`,
                url: m.profile_image_url,
              },
            },
          ]
        : [],
    emails:
      m.primary_email != null
        ? [
            {
              id: `mls-${m.id}`,
              email: m.primary_email,
              primary: true,
            },
          ]
        : [],
    phone_numbers:
      m.primary_phone_number != null
        ? [
            {
              id: `mls-${m.id}`,
              primary: true,
              number: m.primary_phone_number,
              type: null,
            },
          ]
        : [],
  };
  return synthetic;
};

/**
 * Same fallback for the lot relationship. mls_lots only exposes a
 * subset (no `broker` relationship to agents), so the synthesised lot
 * has `broker: null` — callers that depend on broker info should branch
 * on isMls / mls_lot presence rather than read through.
 */
export const getEffectiveLot = (lead: Lead): LotShape | null => {
  if (lead.lot != null) {
    return lead.lot;
  }
  const m = lead.mls_lot;
  if (m == null || m.id == null) {
    return null;
  }
  const synthetic: LotShape = {
    id: m.id,
    offer_type: m.offer_type ?? null,
    sale_price: m.sale_price ?? null,
    lot_reference: m.lot_reference ?? null,
    broker_id: m.broker_id ?? null,
    broker: null,
  };
  return synthetic;
};
